/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'src/hooks/use-boolean';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Chip, Tooltip, Typography } from '@mui/material';
import Label from 'src/components/label';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { usePopover } from 'src/components/custom-popover';
import { useSettingsContext } from 'src/components/settings';
import CustomPopover from 'src/components/custom-popover/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import axiosInstance from 'src/api/axiosInstance';
import {
  getCurrentProject,
  getCurrentServer,
  getMandEndPoint,
  getTemporaryResumeFileName,
} from 'src/helper/server-address';
import { borderRadius, Box, display, fontSize, fontWeight, margin, padding } from '@mui/system';
import { color } from 'framer-motion';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import { decryptData } from 'src/utils/Crypto';
import { formatDate, useDateFormat } from 'src/helper/date-format-provider';
import CandidateAvatarTooltip from './candidate-avatar-tooltip';

export default function CandTableRow({
  row,
  selected,
  onSelectRow,
  onDeleteRow,
  onEditRow,
  onViewRow,
  // onScheduleRow,
  onUnblockRow,
  fullAccess,
  getArrData,
}) {
  const token = sessionStorage.getItem('token');
  const dateFormatPref = useDateFormat();
  const {
    id,
    fname,
    email,
    city,
    mob,
    candStatus,
    resume,
    blocked,
    priSkl,
    isFinal,
    endDate,
    positionName,
    originalPositionName,
    lastUpdtDate,
  } = row;
  const confirm = useBoolean();
  const settings = useSettingsContext();
  const popover = usePopover();
  const quickBlock = useBoolean();
  const quickUnblock = useBoolean();
  const loadingSend = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  const NewBlockSchema = Yup.object().shape({});
  const backgroundCl = settings.themeMode === 'light' ? 'white' : '#202A37';



  const defaultValues = useMemo(
    () => ({
      blockCmt: '',
    }),
    []
  );
  const methods = useForm({
    resolver: yupResolver(NewBlockSchema),
    defaultValues,
  });

  const theme = useTheme();

  const isLight = theme.palette.mode === 'light';

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const truncateFileName = (fileName, maxLength) => {
    if (fileName.length <= maxLength) return fileName;
    const start = fileName.substring(0, maxLength / 2);
    return `${start}...`;
  };

  const onSubmit = handleSubmit(async (data) => {
    loadingSend.onTrue();

    const encryptedId = await fetchEncryptedToken(id, token);
    if (!encryptedId) throw new Error('Failed to encrypt ID');

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      loadingSend.onFalse();
      const formData = new FormData();
      formData.append('blockCmt', data.blockCmt);
      const updtCandBlockUrl = `/rest/candidate/blockCand/${encryptedId}`;
      axiosInstance
        .put(updtCandBlockUrl, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          reset();
          enqueueSnackbar('Candidate Blocked Successfully!');
          getArrData();
        });
    } catch (error) {
      console.error(error);
      loadingSend.onFalse();
    }
  });

  const downloadResume = async (resumePath) => {
    try {
        if (!resumePath) {
            enqueueSnackbar('No resume found.', { variant: 'warning' });
            return;
        }

        const { data } = await axiosInstance.get(
            '/rest/candidate/download-resume',
            {
                params: { path: resumePath },
                headers: { Authorization: `Bearer ${token}` }
            }
        );

        if (data.error) {
            enqueueSnackbar(data.error, { variant: 'warning' });
            return;
        }

        window.open(data.downloadUrl, '_blank', 'noopener');

    } catch (error) {
        const msg = error.response?.data?.error
            || error.response?.data?.message
            || 'Resume download failed.';
        enqueueSnackbar(msg, { variant: 'error' });
        console.error('Resume download failed:', error);
    }
};

  const getStatusChip = () => {
    const statusConfig = {
      New: { bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
      Interviewed: { bg: '#f0f9ff', color: '#075985', dot: '#0ea5e9' },
      'Offer Rejected': { bg: '#fff1f2', color: '#9f1239', dot: '#f43f5e' },
      'Offer Withdrawn': { bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
      'Did not Join': { bg: '#fff7ed', color: '#9a3412', dot: '#f97316' },
      'No Show': { bg: '#fafaf9', color: '#44403c', dot: '#78716c' },
      Rejected: { bg: '#f8fafc', color: '#475569', dot: '#94a3b8' }
    };

    const config =
      statusConfig[candStatus] || {
        bg: '#f1f5f9',
        color: '#64748b',
        dot: '#cbd5e1'
      };

    const chip = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          borderRadius: '12px',
          bgcolor: config.bg,
          border: `1px solid ${config.dot}20`,
          whiteSpace: 'nowrap'
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: config.dot,
            mr: 1
          }}
        />

        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 550,
            color: config.color
          }}
        >
          {candStatus}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>

        {/* Completed → Only Tooltip */}
        {isFinal === 'Complete' ? (
          <Tooltip title="Form Status Completed!" placement="top" arrow>
            {chip}
          </Tooltip>
        ) : (
          <>
            {chip}

            {/* Incomplete → Tooltip + Icon */}
            {isFinal === 'Incomplete' && (
              <Tooltip title="Incomplete Form" placement="top" arrow>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -8,
                    bgcolor: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <PriorityHighIcon
                    fontSize="small"
                    sx={{ color: '#f59e0b' }}
                  />
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    );
  };




  const blockedCandRowStyle = blocked
    ? {
      color: theme.palette.error.dark,
    }
    : {};

  const isDisabled = endDate && new Date(endDate) < new Date();
  return (
    <>
      <Tooltip title={blocked ? 'This candidate is blacklisted.' : ''} placement="top" arrow>
        <TableRow hover selected={selected}>
          {/* below code rested because the styling should be condfirmed once after the final call of the Manager. */}
          {/* <TableCell
            sx={{ ...blockedCandRowStyle, whiteSpace: 'nowrap' }}
            style={{
              position: 'sticky',
              zIndex: 2,
              left: 0,
              backgroundColor: isLight ? 'white' : '#212b36',
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            {fname}
          </TableCell> */}
          <TableCell
            sx={{
              ...blockedCandRowStyle,
              whiteSpace: 'nowrap',
              position: 'sticky',
              left: 0,
              zIndex: 2,
              backgroundColor: isLight ? 'white' : '#212b36',
              transition: 'background-color 0.3s ease',

              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* AVATAR */}
              <CandidateAvatarTooltip
                candidateId={id}
                candidateName={fname}
                rowData={row}
                onView={onViewRow}
              />


              {/* NAME */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 300, // ✅ correct
                  color: blocked ? '#94a3b8' : '#1e293b',
                }}
              >
                {fname}
              </Typography>
            </Box>
          </TableCell>
          <TableCell sx={{ ...blockedCandRowStyle, whiteSpace: 'nowrap' }}>{email}</TableCell>
          <TableCell sx={blockedCandRowStyle}>{city}</TableCell>
          <TableCell sx={{ ...blockedCandRowStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
            {mob}
          </TableCell>
          <TableCell sx={{ ...blockedCandRowStyle, whiteSpace: 'nowrap' }}>
            {priSkl.length > 10 ? (
              <Tooltip title={priSkl} placement="top" arrow>
                {truncateFileName(priSkl, 10)}
              </Tooltip>
            ) : (
              truncateFileName(priSkl, 10)
            )}
          </TableCell>
          {/* <TableCell sx={{ position: 'relative', ...blockedCandRowStyle }}>
            {isFinal === 'Incomplete' ? (
              <>
                <span>New</span>
                <Tooltip title="Incomplete" placement="top" arrow>
                  <Label
                    variant="filled"
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#ffbe4d',
                      position: 'absolute',
                      top: 4,
                      left: 'calc(100% + -85px)',
                      height: 16,
                      px: 0.5,
                      borderBottomLeftRadius: 2,
                      padding: '3px 2px',
                      fontWeight: 'bold',
                      fontSize: '0.50rem',
                    }}
                  >
                    <Iconify icon="mage:exclamation-circle-fill" />
                  </Label>
                </Tooltip>
              </>
            ) : (
              
              <span>{candStatus}</span>
            )}
          </TableCell> */}

          <TableCell>
            {getStatusChip()}
          </TableCell>


          <TableCell sx={{ ...blockedCandRowStyle, whiteSpace: 'nowrap' }}>
            {candStatus === 'Rejected' ? (
              <Tooltip
                title={`Not approved for the ${originalPositionName} position`}
                placement="top"
              >
                <span>Not Approved</span>
              </Tooltip>
            ) : (
              positionName || '-'
            )}
          </TableCell>
          <TableCell sx={{ ...blockedCandRowStyle, textAlign: 'center' }}>
            {resume ? (
              resume.split('/').pop() === getTemporaryResumeFileName() ? (
                <Tooltip title="Temporary Resume" placement="top" arrow>
                  <Button variant="soft" color="error" onClick={() => downloadResume(resume)} className="doNotDisable">
                    Resume
                  </Button>
                </Tooltip>
              ) : (
                <Button variant="soft" color="info" onClick={() => downloadResume(resume)} className="doNotDisable">
                  Resume
                </Button>
              )
            ) : (
              <Tooltip title="No Resume Found!" placement="top" arrow>
                <span>
                  <Button variant="soft" color="info" disabled>
                    Resume
                  </Button>
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={{ ...blockedCandRowStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {lastUpdtDate && lastUpdtDate !== '-' ? formatDate(lastUpdtDate, dateFormatPref) : '-'}
          </TableCell>
          <TableCell
            align="right"
            style={{
              position: 'sticky',
              right: 0, // Sticks to the right
              zIndex: 2,
              backgroundColor: backgroundCl,
            }}
          >
            <IconButton color={popover.open ? 'primary' : 'default'} onClick={popover.onOpen}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
          </TableCell>
        </TableRow>
      </Tooltip>
      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        {/*
        {fullAccess && !isDisabled &&
          (candStatus === 'New' || candStatus === 'Interviewed' || candStatus === 'Exited') &&
          !blocked &&
          isFinal === 'Complete' && (
            <MenuItem
              onClick={() => {
                onScheduleRow();
                popover.onClose();
              }}
            >
              <img
                src="/assets/icons/menu-items/fluent-color-calendar-clock.png"
                alt="view eye"
                className="menu-item-img"
              />
              Schedule
            </MenuItem>
          )}
        */}
        <MenuItem
          onClick={() => {
            onViewRow();
            popover.onClose();
          }}
        >
          <img
            src="/assets/icons/menu-items/mdi--eye.png"
            alt="view eye"
            className="menu-item-img"
          />
          View
        </MenuItem>
        {fullAccess && !isDisabled && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <img
              src="/assets/icons/menu-items/pencil-fill.png"
              alt="Edit pencil"
              className="menu-item-img"
            />
            Edit
          </MenuItem>
        )}
        {fullAccess && !isDisabled ? (
          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <img
              src="/assets/icons/menu-items/solar--trash-bin-trash-bold.png"
              alt="recycle bin"
              className="menu-item-img"
            />
            Delete...
          </MenuItem>
        ) : null}
        {fullAccess && !isDisabled && candStatus !== 'Joined' && (
          <>
            {blocked ? (
              <MenuItem
                onClick={() => {
                  quickUnblock.onTrue();
                  popover.onClose();
                }}
                sx={{ whiteSpace: 'normal' }}
              >
                <Iconify icon="solar:user-block-bold" />
                Remove From Blacklist
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  quickBlock.onTrue();
                  popover.onClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <img
                  src="/assets/icons/menu-items/user-block.png"
                  alt="blacklist user"
                  className="menu-item-img"
                />
                Blacklist
              </MenuItem>
            )}
          </>
        )}
      </CustomPopover>
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Candidate"
        content="Are you sure you want to delete?"
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onDeleteRow();
              confirm.onFalse();
            }}
          >
            Delete...
          </Button>
        }
      />
      <ConfirmDialog
        open={quickUnblock.value}
        onClose={quickUnblock.onFalse}
        title="Unblock"
        content="Are you sure you want to remove this candidate from blacklist?"
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onUnblockRow();
              quickUnblock.onFalse();
            }}
          >
            Remove
          </Button>
        }
      />
      <Dialog
        fullWidth
        maxWidth={false}
        open={quickBlock.value}
        onClose={quickBlock.onFalse}
        PaperProps={{
          sx: { maxWidth: 720 },
        }}
      >
        <FormProvider methods={methods}>
          <DialogTitle>Blacklist Candidate</DialogTitle>
          <DialogContent>
            <RHFTextField name="blockCmt" placeholder="Add Comment" multiline rows={3} />
          </DialogContent>
          <DialogActions>
            <Button
              variant="soft"
              color="error"
              onClick={() => {
                reset();
                quickBlock.onFalse();
              }}
            >
              Cancel
            </Button>

            <LoadingButton
              type="submit"
              variant="soft"
              color="success"
              loading={isSubmitting}
              onClick={() => {
                onSubmit();
                quickBlock.onFalse();
              }}
            >
              Blacklist Candidate
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
}

CandTableRow.propTypes = {
  onDeleteRow: PropTypes.func,
  onEditRow: PropTypes.func,
  onSelectRow: PropTypes.func,
  onViewRow: PropTypes.func,
  // onScheduleRow: PropTypes.func,
  onUnblockRow: PropTypes.func,
  getArrData: PropTypes.func,
  row: PropTypes.object,
  selected: PropTypes.bool,
  fullAccess: PropTypes.bool,
};
